import { formatActivityDate } from '@/dsm-components';
import { PalmettoTag } from '@palmetto/dsm-components';
import { useMemo } from 'react';

import type { AvlAllocationDetail } from 'types';

import { ApprovedVendorAllocationLabels, ApprovedVendorAllocationTagColors } from './allocationLabels';

/** Formats a date-only `IsoDateString` without any local-timezone shift. */
export const formatWindowDate = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

export const sortByStartDateAsc = (rows: AvlAllocationDetail[]): AvlAllocationDetail[] =>
  [...rows].sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0));

interface ScheduleRowProps {
  row: AvlAllocationDetail;
}

const ScheduleRow = ({ row }: ScheduleRowProps) => (
  <div className="grid grid-cols-5 items-center gap-8 border-b border-border-default py-8 last:border-b-0">
    <div className="body-300 text-text-primary">{formatWindowDate(row.startDate)}</div>
    <div className="body-300 text-text-primary">{row.endDate ? formatWindowDate(row.endDate) : 'Open'}</div>
    <div>
      <PalmettoTag
        label={ApprovedVendorAllocationLabels[row.allocationStatus] ?? row.allocationStatus}
        color={ApprovedVendorAllocationTagColors[row.allocationStatus]}
      />
    </div>
    <div className="body-300 break-all text-text-secondary">{row.setByName ?? row.setBy}</div>
    <div className="body-300 text-text-secondary">{formatActivityDate(row.setAt)}</div>
  </div>
);

export interface AllocationScheduleSectionProps {
  allocationDetails: AvlAllocationDetail[] | undefined;
}

/** Read-only; archived rows (support corrections) are hidden. New windows are added from the edit form. */
export const AllocationScheduleSection = ({ allocationDetails }: AllocationScheduleSectionProps) => {
  const activeRows = useMemo(
    () => sortByStartDateAsc((allocationDetails ?? []).filter((row) => !row.archived)),
    [allocationDetails],
  );

  return (
    <div className="mb-16 flex flex-col gap-8">
      <h2 className="text-left heading-200 text-text-primary">Allocation Schedule</h2>

      {activeRows.length === 0 ? (
        <div className="body-300 text-text-tertiary">No allocation windows recorded yet.</div>
      ) : (
        <div className="flex flex-col">
          <div className="grid grid-cols-5 gap-8 border-b border-border-default pb-4 body-bold-300 text-text-secondary">
            <div>Start</div>
            <div>End</div>
            <div>Status</div>
            <div>Set by</div>
            <div>Set at</div>
          </div>
          {activeRows.map((row) => (
            <ScheduleRow key={row.id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
};
